import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PublicNav from '@/components/PublicNav';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function TripMemoir() {
  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMemoir();
  }, [slug]);

  const loadMemoir = async () => {
    try {
      const { data } = await axios.get(`${API}/public/trip/${slug}/memoir`);
      setTrip(data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Memoir not found' : 'Failed to load memoir');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <ZLoadingBar accent="#10B981" loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-16" data-testid="trip-memoir-error">
        <PublicNav />
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-[#b0b4c0] font-serif text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-16 pb-20" data-testid="trip-memoir-page">
      <PublicNav />
      
      {/* Hero - Full Bleed Cover */}
      {trip.cover_image && (
        <div className="relative w-full h-[50vh] overflow-hidden">
          <img 
            src={getImageUrl(trip.cover_image)} 
            alt={trip.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/30 to-transparent" />
        </div>
      )}
      
      {/* Title Section */}
      <div className={`px-6 md:px-12 ${trip.cover_image ? '-mt-24 relative z-10' : 'pt-12'}`}>
        <div className="max-w-2xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#f0f0f5]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="trip-memoir-title"
          >
            {trip.title}
          </h1>
          <p className="font-mono text-sm text-[#b0b4c0] mt-4">
            {formatDate(trip.start_date)}
            {trip.end_date && ` — ${formatDate(trip.end_date)}`}
          </p>
        </div>
      </div>

      {/* Placeholder Content */}
      <div className="px-6 md:px-12 pt-16">
        <div className="max-w-xl mx-auto text-center">
          {/* Thin horizontal rule */}
          <div className="w-16 h-px bg-white/[0.06] mx-auto mb-8" />
          
          {/* Coming Soon Text */}
          <p 
            className="text-xl text-[#b0b4c0] italic"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="trip-memoir-placeholder"
          >
            Memoir coming soon.
          </p>
          
          {/* Dates */}
          <p className="font-mono text-xs text-[#b0b4c0]/50 mt-6">
            {formatDate(trip.start_date)}
            {trip.end_date && ` — ${formatDate(trip.end_date)}`}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-12 mt-16">
        <p className="text-xs tracking-[0.3em]" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'rgba(255,255,255,0.06)' }}>
          WERNER LABS
        </p>
      </div>
    </div>
  );
}
